/**
 * Подарить другу: срок, поздравление, оплата — и ссылка для друга.
 *
 * Оплата та же, что у тарифов (звёзды или Lava), только с пометкой
 * «в подарок»: вместо подписки сервер выпускает сертификат. Друг получит
 * отдельную подписку, когда перейдёт по ссылке; своя подписка покупателя
 * не меняется.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { usePaymentMethods, usePlans } from '@/entities/subscription/queries';
import { TelegramPrompt } from '@/features/guest/TelegramPrompt';
import { copyText, giftLink, sendGift, useGifts } from '@/features/gift/gift';
import { usePayLava } from '@/features/payment/usePayLava';
import { usePayStars } from '@/features/payment/usePayStars';
import {
  PAY_LABEL,
  perMonth,
  priceIn,
  priceOf,
  monthsOf,
  usable,
  withAvailability,
  type MethodId,
} from '@/pages/plans/PlansPage';
import type { Period, Plan } from '@/shared/api/schemas';
import { formatPeriod } from '@/shared/lib/format';
import { isGuest } from '@/shared/lib/guest';
import { useBackButton } from '@/shared/lib/useBackButton';
import { Notice, Screen } from '@/shared/ui/Screen';
import { Button } from '@/shared/ui/kit/Button';
import { Card, GroupCaption } from '@/shared/ui/kit/Card';
import { Icon } from '@/shared/ui/kit/Icon';
import { Row } from '@/shared/ui/kit/Row';
import { Sheet } from '@/shared/ui/kit/Sheet';

import plans from '../plans/PlansPage.module.css';
import styles from './Gift.module.css';

const MESSAGE_LIMIT = 256;

interface Ready {
  id: string;
  code: string;
  title: string;
  message: string | null;
}

/** Окно «Подарок готов»: ссылка, копирование и отправка другу. */
export function GiftReady({ ready, onClose }: { ready: Ready | null; onClose: () => void }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const link = ready ? giftLink(ready.code) : '';

  const copy = async () => {
    if (await copyText(link)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Sheet open={ready !== null} title="Подарок готов" onClose={onClose} fit>
      <div className={styles.card}>
        <span className={styles.cardBrand}>ANROYAL VPN</span>
        <span className={styles.cardTitle}>{ready?.title}</span>
        {ready?.message ? <span className={styles.cardMessage}>«{ready.message}»</span> : null}
      </div>
      <p className={styles.lead}>
        Отправьте подарок другу — подписка появится у него после перехода в бота.
      </p>
      <div className={styles.linkRow}>
        <span className={styles.link}>{link.replace('https://', '')}</span>
        <button type="button" className={styles.copy} aria-label="Скопировать ссылку" onClick={() => void copy()}>
          <Icon name={copied ? 'check' : 'copy'} size={18} />
        </button>
      </div>
      <div className={plans.pay}>
        <Button
          block
          onClick={() => {
            if (ready) void sendGift(ready, `Дарю тебе ${ready.title}`);
          }}
        >
          <Icon name="gift" size={18} />
          Отправить другу
        </Button>
      </div>
      <Button
        block
        variant="quiet"
        onClick={() => {
          onClose();
          void navigate('/gifts');
        }}
      >
        Мои подарки
      </Button>
      <p className={plans.payNote}>Ссылка не сгорает и сработает один раз. Она также пришла вам в бота.</p>
    </Sheet>
  );
}

export function GiftPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const plansQuery = usePlans();
  const stars = usePayStars();
  const lava = usePayLava();
  const methodFlags = usePaymentMethods();
  const METHODS = withAvailability(methodFlags.data);
  const guest = isGuest();
  const gifts = useGifts();
  const giftItems = guest ? [] : (gifts.data?.items ?? []);
  const waiting = giftItems.filter((g) => g.status === 'pending').length;

  const [chosen, setChosen] = useState<{ plan: Plan; period: Period } | null>(null);
  const [message, setMessage] = useState('');
  const [methodId, setMethodId] = useState<MethodId>('sbp');
  const [ready, setReady] = useState<Ready | null>(null);
  const [pending, setPending] = useState(false);
  const [prompt, setPrompt] = useState(false);
  // Что оплачивается — запоминается на время оплаты: шторка выбора к её
  // концу уже закрыта, а окну «Подарок готов» нужны срок и поздравление.
  const [intent, setIntent] = useState<{ title: string; message: string | null } | null>(null);

  const phase =
    stars.phase !== 'idle'
      ? stars.phase
      : lava.phase === 'creating'
        ? ('creating' as const)
        : lava.phase === 'waiting'
          ? ('waiting' as const)
          : ('idle' as const);
  const error = stars.error ?? lava.error;

  const open = (plan: Plan, period: Period) => {
    stars.reset();
    lava.reset();
    setMethodId(METHODS.find((m) => usable(m, period))?.id ?? 'stars');
    setChosen({ plan, period });
  };

  const method = METHODS.find((m) => m.id === methodId) ?? METHODS[0];
  const methodPrice = chosen && method ? priceIn(chosen.period, method.currency) : undefined;
  const canPay = Boolean(chosen && method && usable(method, chosen.period));

  const startPayment = () => {
    if (guest) {
      setPrompt(true);
      return;
    }
    if (!chosen || !method || !canPay) return;
    const gift = chosen;
    const request = {
      plan_code: gift.plan.code,
      period_code: gift.period.code,
      extend_subscription_id: null,
      gift: true,
      gift_message: message.trim() || null,
    };
    setIntent({
      title: `VPN на ${formatPeriod(gift.period.code, gift.period.duration_days)}`,
      message: request.gift_message,
    });
    const run = method.id === 'stars' ? stars.pay(request) : lava.pay(request, method.id);
    void run.then(async (ok) => {
      if (!ok) return;
      await queryClient.invalidateQueries({ queryKey: ['gifts'] });
      setChosen(null);
      setMessage('');
    });
  };

  // Итог оплаты хук отдаёт состоянием: ссылка есть — «Подарок готов»,
  // ещё нет (оплата прошла, сертификат в пути) — «ссылка придёт в бота».
  const paid = stars.result ?? lava.result;
  useEffect(() => {
    if (!paid) return;
    const payment = paid.payment;
    if (payment?.gift_id && payment.gift_code) {
      setReady({
        id: payment.gift_id,
        code: payment.gift_code,
        title: intent?.title ?? 'VPN в подарок',
        message: intent?.message ?? null,
      });
    } else {
      setPending(true);
    }
  }, [paid]);

  const closeReady = () => {
    setReady(null);
    setPending(false);
    stars.reset();
    lava.reset();
  };

  // «Назад» сначала закрывает открытое окно: со ссылкой на подарок или
  // с ожиданием оплаты уходить с экрана нельзя случайно.
  useBackButton(() => {
    if (ready !== null || pending) closeReady();
    else if (chosen !== null) setChosen(null);
    else void navigate('/subscriptions');
  });

  if (!plansQuery.isLoading && !plansQuery.error && plansQuery.data?.length === 0) {
    return <Notice header="Тарифов пока нет" description="Загляните чуть позже." />;
  }

  return (
    <Screen isLoading={plansQuery.isLoading} error={plansQuery.error} onRetry={() => void plansQuery.refetch()}>
      <h1 className={plans.head}>Подарить другу</h1>
      <p className={styles.intro}>Друг получит отдельную подписку по ссылке. Ваша подписка не изменится.</p>

      {giftItems.length > 0 ? (
        <Card>
          <Row
            icon="gift"
            title="Мои подарки"
            subtitle={waiting > 0 ? `Ждут активации: ${waiting}` : 'Все активированы'}
            value={String(giftItems.length)}
            onClick={() => void navigate('/gifts')}
          />
        </Card>
      ) : null}

      {plansQuery.data?.map((plan) => {
        const base = plan.periods.find((p) => monthsOf(p) === 1);
        return (
          <div key={plan.code} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <GroupCaption>Срок подарка</GroupCaption>
            {plan.periods.map((period) => {
              const price = priceOf(period);
              const extra = perMonth(period, base);
              return (
                <button key={period.code} type="button" className={plans.period} onClick={() => open(plan, period)}>
                  <span className={plans.body}>
                    <span className={plans.name}>
                      {formatPeriod(period.code, period.duration_days)}
                      {extra?.save ? <span className={plans.save}>−{extra.save}%</span> : null}
                    </span>
                    <span className={plans.monthly}>
                      {plan.device_limit ? `до ${plan.device_limit} устройств` : 'без ограничения устройств'}
                    </span>
                  </span>
                  <span className={plans.right}>
                    <span className={plans.price}>{price?.formatted ?? '—'}</span>
                  </span>
                </button>
              );
            })}
          </div>
        );
      })}

      <ul className={styles.rules}>
        <li>Ссылка не сгорает — друг активирует её, когда удобно</li>
        <li>Сработает один раз, у одного человека</li>
        <li>Если у друга уже есть подписка, подарок станет второй</li>
      </ul>

      <Sheet
        open={chosen !== null}
        title={chosen ? `Подарок на ${formatPeriod(chosen.period.code, chosen.period.duration_days)}` : ''}
        onClose={() => setChosen(null)}
        fit
      >
        <label className={styles.field}>
          <span className={plans.caption}>Поздравление — необязательно</span>
          <textarea
            className={styles.textarea}
            rows={3}
            maxLength={MESSAGE_LIMIT}
            value={message}
            placeholder="С днём рождения!"
            onChange={(event) => setMessage(event.target.value)}
          />
          <span className={styles.counter}>
            {message.length} / {MESSAGE_LIMIT}
          </span>
        </label>

        <p className={plans.caption}>Способ оплаты</p>
        <div className={plans.methods} role="radiogroup" aria-label="Способ оплаты">
          {METHODS.map((m) => {
            const price = chosen ? priceIn(chosen.period, m.currency) : undefined;
            const enabled = chosen ? usable(m, chosen.period) : false;
            const selected = enabled && m.id === methodId;
            return (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={!enabled}
                className={[plans.method, selected ? plans.selected : ''].filter(Boolean).join(' ')}
                onClick={() => setMethodId(m.id)}
              >
                <Icon name={m.icon} size={22} />
                <span className={plans.methodName}>{m.title}</span>
                <span className={plans.methodPrice}>
                  {!m.available ? (
                    'скоро'
                  ) : price && m.currency === 'XTR' ? (
                    <>
                      {price.amount_minor}
                      <Icon name="star" size={11} />
                    </>
                  ) : (
                    (price?.formatted ?? '—')
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <div className={plans.pay}>
          <Button block disabled={!guest && (!canPay || phase !== 'idle')} onClick={startPayment}>
            {guest ? (
              'Подарить в Telegram'
            ) : phase !== 'idle' ? (
              PAY_LABEL[phase]
            ) : canPay && methodPrice ? (
              method?.currency === 'XTR' ? (
                <>
                  Оплатить подарок · {methodPrice.amount_minor}
                  <Icon name="star" size={17} />
                </>
              ) : (
                `Оплатить подарок · ${methodPrice.formatted}`
              )
            ) : (
              'Оплата недоступна'
            )}
          </Button>
        </div>
        {phase === 'waiting' ? (
          <div className={plans.waiting}>
            <p className={plans.payNote}>
              Оплатите на открывшейся странице — ссылка на подарок придёт в бота и появится здесь.
            </p>
            <div className={plans.waitingActions}>
              <Button size="s" variant="quiet" onClick={() => lava.reopen()}>
                Открыть оплату снова
              </Button>
              <Button size="s" variant="quiet" onClick={() => lava.cancel()}>
                Отменить
              </Button>
            </div>
          </div>
        ) : error ? (
          <p className={`${plans.payNote} ${plans.payError}`} role="alert">
            {error}
          </p>
        ) : (
          <p className={plans.payNote}>После оплаты вы получите ссылку для друга</p>
        )}
      </Sheet>

      <Sheet open={pending} title="Оплата прошла" onClose={closeReady} fit>
        <p className={styles.lead}>
          Подарок оформляется — ссылка придёт в бота через минуту и появится в «Мои подарки».
        </p>
        <Button
          block
          onClick={() => {
            closeReady();
            void navigate('/gifts');
          }}
        >
          Мои подарки
        </Button>
      </Sheet>

      <GiftReady ready={ready} onClose={closeReady} />

      <TelegramPrompt open={prompt} action="Подарить подписку" onClose={() => setPrompt(false)} />
    </Screen>
  );
}
